<template>
  <div>
    <div class="dashboard-page-title">
      <div class="container-fluid">
        <h1>
          Bookings
          <span v-if="client">&ndash; {{ client.name }}</span>
          <span v-if="manager">&ndash; {{ manager.company }}</span>
          <help-link v-if="!$vlastMode" anchor="bookings" />
        </h1>
      </div>
    </div>

    <div class="container-fluid">

      <div class="dashboard-results-details">
        <div class="w-full flex flex-wrap justify-between spacer-b-2">
          <div class="count"><span>{{ pagination.total }}</span> Bookings</div>
          <booking-list-sort
            :set-filter-mutation="setFilter"
            :filters="filters"
          />
        </div>
      </div>

      <booking-list-filters
        :set-filter-mutation="setFilter"
        :filters="filters"
        :apply-filters="handleApplyFiltersCallback">
        <ladda-button
          slot="button"
          :click="handleApplyFiltersCallback"
          type="submit"
          :disabled="$userIsInactiveManager"
          class="btn btn-primary pull-right">
          Search
        </ladda-button>
      </booking-list-filters>

      <div class="dashboard-block">
        <ul class="nav nav-tabs" role="tablist">
          <li :class="{ 'active': status === 'active' }">
            <router-link :to="{ params: { status: 'active' }, query: $route.query }">{{ $userIsAdmin || $userIsIntern || this.$user.role === 'sales' ? 'View All (Active)' : 'Active' }}</router-link>
          </li>
          <li v-if="$userIsAdmin || $userIsIntern || this.$user.role === 'sales'" :class="{ 'active': status === 'event' }">
            <router-link :to="{ params: { status: 'event' }, query: $route.query }">In Person</router-link>
          </li>
          <li v-if="$userIsAdmin || $userIsIntern || this.$user.role === 'sales'" :class="{ 'active': status === 'campaign' }">
            <router-link :to="{ params: { status: 'campaign' }, query: $route.query }">Online</router-link>
          </li>
          <li v-if="$vlastMode && $offersModuleEnabled && ($userIsAdmin || $userIsManager || $userIsTalent)" :class="{ 'active': status === 'offer' }">
            <router-link :to="{ params: { status: 'offer' }, query: $route.query }">Offer</router-link>
          </li>
          <li v-if="$userIsAdmin || $userIsIntern || this.$user.role === 'sales'" :class="{ 'active': status === 'completed-event' }">
            <router-link :to="{ params: { status: 'completed-event' }, query: $route.query }">Completed (In Person)</router-link>
          </li>
          <li v-if="$userIsAdmin || $userIsIntern || this.$user.role === 'sales'" :class="{ 'active': status === 'completed-campaign' }">
            <router-link :to="{ params: { status: 'completed-campaign' }, query: $route.query }">Completed (Online)</router-link>
          </li>
          <li v-if="!$vlastMode && ($userIsAdmin || $userIsIntern)" :class="{ 'active': status === 'packages' }">
            <router-link :to="{ params: { status: 'packages' }, query: $route.query }">Packages</router-link>
          </li>
          <li :class="{ 'active': status === 'archived' }">
            <router-link :to="{ params: { status: 'archived' }, query: $route.query }">Archived</router-link>
          </li>
        </ul>

        <pickstar-loading v-if="fetchingBookings" class="m-t-lg"/>

        <template v-else>
          <no-results v-if="$userIsInactiveManager" class="text-center">
            <div>
              You will be able to view {{ $t('bookings') }} once {{ clientName }} has approved your account. In the meantime,
              build out your <router-link :to="{ name: 'profile.show' }">profile</router-link> or start
              <router-link :to="{ name: 'talent.index' }">adding your {{ $starLabelPlural }} for review!</router-link>
            </div>
          </no-results>
          <no-results v-else-if="haveNoBookings">
            <div>There are no bookings.</div>

            <button v-if="haveAppliedFilters" @click="handleResetFiltersCallback" class="btn btn-default btn-sm m-t-sm">Reset Filters</button>
          </no-results>

          <ul class="list-items">
            <booking-list-item
              v-for="booking in bookings"
              :key="booking.id"
              :booking="booking"/>
          </ul>

          <lazy-loading
            v-if="!$userIsInactiveManager"
            class="text-center"
            :loading="loading"
            :reached-bottom="handleGetNextBookingsCallback"
            ref="lazyLoading">
            <pickstar-loading slot="loading"/>
            <span slot="finished"></span>
          </lazy-loading>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapState, mapGetters } from 'vuex'
import { appliesFilters, handleServerErrors, fetchResourceScope } from 'mixins'
import LazyLoading from 'codium-lazy-loading'
import BookingListFilters from 'components/booking/BookingListFilters'
import BookingListItem from './BookingListItem'
import BookingListSort from 'components/booking/BookingListSort'
import HelpLink from 'components/globals/HelpLink'

export default {
  mixins: [fetchResourceScope, appliesFilters, handleServerErrors],

  props: {
    status: {
      required: false,
      default: 'active'
    }
  },

  components: {
    LazyLoading,
    BookingListItem,
    BookingListFilters,
    BookingListSort,
    HelpLink
  },

  data () {
    return {
      fetchingBookings: false
    }
  },

  computed: {
    ...mapState('booking', ['bookings', 'pagination', 'filters']),
    ...mapGetters('booking', ['initialFiltersState']),
    ...mapState('app', ['loading']),

    haveNoBookings () {
      return this.bookings.length === 0
    }
  },

  watch: {
    '$route': {
      handler () {
        if (!this.$userIsInactiveManager) {
          this.handleGetBookingsCallback()
        }
      }
    }
  },

  methods: {
    ...mapActions('booking', ['getBookings', 'getNextBookings', 'setFilter', 'resetFilters']),

    handleGetBookingsCallback () {
      // when click on COMPLETED tab for the first time,
      // set the default completed booking order to desc
      if ((this.$route.params.status === 'completed-campaign' || this.$route.params.status === 'completed-event') && !this.$route.query.order) {
        this.setFilter({ filter: 'order', value: 'desc' })
      }

      this.fetchingBookings = true

      return this.getBookings(this.queryParams)
        .catch(this.handleServerErrorsCallback)
        .finally(() => {
          this.fetchingBookings = false

          this.$nextTick(() => {
            if (!this.$refs.lazyLoading) {
              return
            }

            this.$refs.lazyLoading.$emit('reset')
          })
        })
    },

    handleGetNextBookingsCallback () {
      return this.getNextBookings(this.queryParams)
        .catch(this.handleServerErrorsCallback)
    }
  }
}
</script>
